import _fetch from 'utils/fetch';
import { AuthState } from 'context/auth';

export interface CustomVMProfile {
  min_cpu_platform: string;
  machine_type: string;
  gpu_count: number;
  gpu_type: string;
  disk_size: number;
}

export interface StandardVMProfile {
  profile_id: number;
  disk_size: number;
}

function makeRequest(
  method: string,
  auth: AuthState,
  url: string,
  body: any,
  responseType: string
): any {
  const credential = auth.getCredentialOrPromptLogin();
  if (!credential) {
    return null;
  }

  return {
    url: `${process.env.REACT_APP_API_HOST}${url}`,
    options: {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${credential}`,
      },
      body: body === null ? body : JSON.stringify(body),
    },
    responseReturnType: responseType,
  };
}

function postJson(
  auth: AuthState,
  url: string,
  body: any,
  responseType: string = 'json'
): any {
  return makeRequest('post', auth, url, body, responseType);
}

function putJson(
  auth: AuthState,
  url: string,
  body: any,
  responseType: string = 'json'
): any {
  return makeRequest('put', auth, url, body, responseType);
}

function getJson(auth: AuthState, url: string): any {
  const credential = auth.getCredentialOrPromptLogin();
  if (!credential) {
    return null;
  }

  return {
    url: `${process.env.REACT_APP_API_HOST}${url}`,
    options: {
      method: 'get',
      headers: {
        Authorization: `Bearer ${credential}`,
      },
    },
    responseReturnType: 'json',
  };
}

export function getSandboxTerms(
  auth: AuthState,
  sandboxId: string | undefined
) {
  const req = getJson(auth, `/sandbox/${sandboxId}/document/tc`);
  return _fetch(req);
}

export function postErrorReport(auth: AuthState, report: any) {
  const req = postJson(auth, '/user/report-error', report, 'text');
  return _fetch(req);
}

export function postAcceptTerms(
  auth: AuthState,
  sandboxId: string | undefined
) {
  const req = postJson(
    auth,
    `/sandbox/${sandboxId}/user/accept-terms`,
    null,
    'text'
  );
  return _fetch(req);
}

export function getVM(auth: AuthState, sandboxId: string | undefined) {
  const req = getJson(auth, `/sandbox/${sandboxId}/vm`);
  return _fetch(req);
}

export function getVMProfiles(auth: AuthState, sandboxId: string | undefined) {
  const req = getJson(auth, `/sandbox/${sandboxId}/vm-profiles`);
  return _fetch(req);
}

export function getVMRestrictions(
  auth: AuthState,
  sandboxId: string | undefined
) {
  const req = getJson(auth, `/sandbox/${sandboxId}/vm-restrictions`);
  return _fetch(req);
}

export function getVMMachineTypes(
  auth: AuthState,
  sandboxId: string | undefined,
  machineFamily: string
) {
  const req = getJson(
    auth,
    `/sandbox/${sandboxId}/vm-machine-types?family=${machineFamily}`
  );
  return _fetch(req);
}

export function getVMAcceleratorTypes(
  auth: AuthState,
  sandboxId: string | undefined
) {
  const req = getJson(auth, `/sandbox/${sandboxId}/vm-accelerator-types`);
  return _fetch(req);
}

export function getVMDiskLimits(
  auth: AuthState,
  sandboxId: string | undefined
) {
  const req = getJson(auth, `/sandbox/${sandboxId}/vm/disk-limits`);
  return _fetch(req);
}

export function postStartVM(
  auth: AuthState,
  sandboxId: string | undefined,
  vmProfile: StandardVMProfile | CustomVMProfile
) {
  const req = postJson(auth, `/sandbox/${sandboxId}/vm`, vmProfile);
  return _fetch(req);
}

export function putShutdownVM(auth: AuthState, sandboxId: string | undefined) {
  const req = putJson(auth, `/sandbox/${sandboxId}/vm`, { state: 'shutdown' });
  return _fetch(req);
}
